/**
 * Input adapter: calls use cases (no InputPort; contracts.md).
 * Plan 10: Pointer Events for DnD gems; click → PlaceBuilding, tower select.
 */

import type { FieldId, BuildingType, GemType, Cell } from "../../domain/types.js";
import { MAP_COLS, MAP_ROWS, isFieldOnLand } from "../../application/global-map-fields.js";
import { BuildingType as BT, GemType as GT } from "../../domain/types.js";
import type { GemSlot } from "../../application/types.js";
import type { StartBattleResult } from "../../application/types.js";
import type { GameSession } from "../../domain/session.js";
import type { RenderPort } from "../../application/ports.js";
import { sessionToView } from "../../application/state-view.js";
import {
  pointToGameCell,
  pointToMapCell,
  // pointToSpeedButton removed — speed buttons moved to DOM HUD (Phase 2.2)
  pointToInventorySlot,
  pointToTowerSlot,
  pointToDropZone,
  GAME_GRID_W,
} from "../render/grid-math.js";
import { hitTestEditorCell } from "../render/canvas-render-adapter.js";
import { t } from "../../i18n/index.js";

export interface GameLoopStart {
  start(): void;
}

export interface MapGeometryInput {
  fieldIds: FieldId[];
  rows: number;
  cols: number;
  playableFieldIds?: FieldId[];
}

const KEY_TO_GEM: Record<string, GemType> = {
  Digit1: GT.Yellow,
  Digit2: GT.Orange,
  Digit3: GT.Red,
  Digit4: GT.Purple,
  Digit5: GT.Green,
  Digit6: GT.Blue,
  Digit7: GT.Cyan,
  Digit8: GT.Lime,
  Numpad1: GT.Yellow,
  Numpad2: GT.Orange,
  Numpad3: GT.Red,
  Numpad4: GT.Purple,
  Numpad5: GT.Green,
  Numpad6: GT.Blue,
  Numpad7: GT.Cyan,
  Numpad8: GT.Lime,
};

export interface InputAdapterOptions {
  startBattle?: (fieldId: FieldId) => StartBattleResult;
  /** Opens the battle settings screen for a field (replaces direct startBattle on map). */
  openBattleSettings?: (fieldId: FieldId) => void;
  /** Selects a field on the map (highlights it and shows the CTA bar) without opening settings. */
  selectField?: (fieldId: FieldId) => void;
  gameLoop: GameLoopStart;
  canvas?: HTMLCanvasElement | null;
  placeBuilding?: (cell: { row: number; col: number }, buildingType: BuildingType) => { ok: true } | { code: string };
  getSession?: () => GameSession | null;
  getScene?: () => "map" | "game" | "settings" | string;
  getMapFieldIds?: () => FieldId[];
  getMapGeometry?: () => MapGeometryInput;
  goToMapButton?: HTMLElement | null;
  onExitToMap?: () => void | Promise<void>;
  goToMap?: () => void;
  createGem?: (gemType: GemType) => { ok: true } | { code: string };
  placeGem?: (gemId: string, towerId: string, slotIndex: number) => { ok: true } | { code: string };
  removeGemFromTower?: (towerId: string, slotIndex: number) => { ok: true } | { code: string };
  swapGemsBetweenTowers?: (towerId1: string, towerId2: string) => { ok: true } | { code: string };
  castGemBomb?: (gemId: string, cell: { row: number; col: number }) => { ok: true; damage: number; hits: number; refunded: number } | { code: string };
  updateViewWithDrag?: (dragState: { gem: { id: string; type: string; level: number }; screenX: number; screenY: number } | null) => void;
  upgradeGem?: (slot: GemSlot) => { ok: true } | { code: string };
  duplicateGem?: (slot: GemSlot) => { ok: true } | { code: string };
  combineGems?: (gemAId: string, gemBId: string) => { ok: true; resultGemId: string } | { code: string };
  recycleGem?: (slot: GemSlot) => { ok: true } | { code: string };
  pause?: () => void;
  resume?: () => void;
  cycleSpeed?: () => void;
  setSpeed?: (speed: 0 | 1 | 3) => void;
  /** RenderPort for hit-testing skills panel buttons and settings scene buttons. */
  render?: RenderPort;
  onSkillButtonClick?: (skillId: number) => void;
  onSkillsPanelToggle?: () => void;
  onSkillsPanelClose?: () => void;
  /** Returns true when the skills panel is open (backdrop absorbs clicks). */
  getSkillsPanelOpen?: () => boolean;
  onStatsPanelToggle?: () => void;
  onStatsPanelClose?: () => void;
  /** Returns true when the stats panel is open. */
  getStatsPanelOpen?: () => boolean;
  /** Settings scene callbacks */
  onStageSettingSelectOption?: (kind: "hp" | "waves", index: number) => void;
  onStageSettingToggle?: (kind: "extraBanishCost" | "healOnReturn") => void;
  onStageSettingStart?: () => void;
  onStageSettingCancel?: () => void;
  demolishTower?: (towerId: string) => { refund: number } | { code: "NotFound" };
  /** Tutorial: called when the player clicks «Next» button. */
  onTutorialNext?: () => void;
  /** Tutorial: called when the player clicks «Skip» button. */
  onTutorialSkip?: () => void;
  /** Returns current tutorial step (-1 = done, 0-4 = active). */
  getTutorialStep?: () => number;
  /** Story: called when player advances to next panel. */
  onStoryNext?: () => void;
  /** Story: called when player skips a story beat. */
  onStorySkip?: () => void;

  // Editor scene
  openEditor?: (fieldId: FieldId) => void;
  editorHandleClick?: (cell: Cell, mode: "waypoint" | "block") => void;
  editorSetHoverCell?: (cell: Cell | null) => void;
  editorSetMode?: (mode: "waypoint" | "block") => void;
  editorRemoveLastWaypoint?: () => void;
  editorClearWaypoints?: () => void;
  editorCloseEditor?: (save: boolean) => void;
  /** Returns current editor mode or null when editor is not open. */
  getEditorMode?: () => "waypoint" | "block" | null;
}

export interface InputAdapter {
  attach?(): void;
  /** Removes all listeners registered by attach(). Safe to call multiple times. */
  dispose(): void;
  getPlacementMode(): "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine";
  /** Set placement mode from outside (e.g. DOM HUD button). Triggers a render update. */
  setPlacementMode(mode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine"): void;
  /** Returns the currently selected tower id (or null). Used by DOM HUD action buttons. */
  getSelectedTowerId(): string | null;
  /**
   * Initiate an inventory-gem drag that started on a DOM element (not canvas).
   * Sets dragSource and updates drag ghost view. Document-level pointermove/up
   * listeners (added in attach()) will handle the rest of the gesture.
   */
  startInventoryDrag(gemId: string, screenX: number, screenY: number): void;
  /**
   * Select an inventory gem for combine (commit 5).
   * First call selects primary; second call selects secondary.
   * Clicking the same gem twice clears the selection.
   */
  selectInventoryGem(gemId: string): void;
  /** Trigger combine with the currently selected inventory gems. */
  triggerCombine(): void;
  /** Combine two inventory gems directly, e.g. DOM inventory drag/drop. */
  combineInventoryGems(gemAId: string, gemBId: string): void;
}

export function createInputAdapter(options: InputAdapterOptions): InputAdapter {
  const {
    startBattle,
    openBattleSettings,
    selectField,
    gameLoop,
    canvas,
    placeBuilding,
    getSession,
    getScene,
    getMapFieldIds,
    getMapGeometry,
    goToMapButton,
    onExitToMap,
    goToMap,
    createGem,
    placeGem,
    removeGemFromTower,
    swapGemsBetweenTowers,
    updateViewWithDrag,
    upgradeGem,
    duplicateGem,
    combineGems,
    recycleGem,
    pause,
    resume,
    setSpeed,
    render,
    onSkillButtonClick,
    onSkillsPanelToggle,
    onSkillsPanelClose,
    getSkillsPanelOpen,
    onStatsPanelToggle,
    onStatsPanelClose,
    getStatsPanelOpen,
    onStageSettingSelectOption,
    onStageSettingToggle,
    onStageSettingStart,
    onStageSettingCancel,
    demolishTower,
    onTutorialNext,
    onTutorialSkip,
    getTutorialStep,
    onStoryNext,
    onStorySkip,
    openEditor,

    editorHandleClick,
    editorSetHoverCell,
    editorSetMode,
    editorRemoveLastWaypoint,
    editorClearWaypoints,
    editorCloseEditor,
    getEditorMode,
  } = options;

  const exitOrGoToMap = onExitToMap ?? goToMap;

  let selectedTowerId: string | null = null;
  let placementMode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine" = "Tower";

  // Combine selection: up to 2 inventory gems selected via click (commit 5).
  let selectedInvGemA: string | null = null;
  let selectedInvGemB: string | null = null;
  let combineArmed = false;

  function clearCombineSelection(): void {
    selectedInvGemA = null;
    selectedInvGemB = null;
    combineArmed = false;
  }

  function combineSelectedInventoryGems(gemAId: string, gemBId: string): void {
    if (!combineGems || gemAId === gemBId) return;
    const result = combineGems(gemAId, gemBId);
    if ("ok" in result && result.ok) {
      clearCombineSelection();
    } else {
      combineArmed = false;
      console.debug("[combine] error:", (result as { code: string }).code);
    }
  }

  // ── B7: Pinch-to-zoom state ───────────────────────────────────────────────
  /** All active pointers tracked by pointerId → client position. */
  const _pinchPointers = new Map<number, { x: number; y: number }>();
  let _pinchInitialDist = 0;
  let _pinchInitialScale = 1;
  let _pinchInitialAnchorWorldX = 0;
  let _pinchInitialAnchorWorldY = 0;
  // ─────────────────────────────────────────────────────────────────────────

  // ── B12: Long-press tower-stats on touch ─────────────────────────────────
  /** Whether the touch device media query matches (coarse pointer). */
  const _isCoarsePointer = typeof matchMedia !== "undefined"
    && matchMedia("(hover: none) and (pointer: coarse)").matches;
  /** Timer for long-press show of canvas tower-stats tooltip. */
  let _longPressTimer: ReturnType<typeof setTimeout> | null = null;
  /** Start position of the current touch, for move-cancel threshold. */
  let _longPressStartX = 0;
  let _longPressStartY = 0;
  /** True when the tower-stats tooltip was opened by a long-press (touch). */
  let _touchTooltipActive = false;
  // ─────────────────────────────────────────────────────────────────────────

  type DragSource = { kind: "inventory"; gemId: string } | { kind: "tower"; towerId: string; slotIndex: number; gemId: string };
  let dragSource: DragSource | null = null;
  /** Shift state captured from the last pointer event — read by handleClick for Shift+Click shortcuts. */
  let lastPointerShiftKey = false;

  function currentScene(): string {
    return getScene?.() ?? (getSession?.() ? "game" : "map");
  }

  /**
   * Convert a client-space pointer position to logical canvas bitmap coords
   * (1280×720 space, accounting for CSS stretch AND zoom transform).
   *
   * Two-step inverse:
   *  1. CSS stretch: clientXY → CSS-canvas px → bitmap px (÷ cssScale)
   *  2. Zoom inverse: bitmap-screen px → world px  ((bitmapPx - offset) / zoomScale)
   */
  function toCanvasCoords(e: PointerEvent): { canvasX: number; canvasY: number } | null {
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    // Step 1: CSS stretch (canvas.width is the bitmap width e.g. 1280)
    const cssScaleX = canvas.width / rect.width;
    const cssScaleY = canvas.height / rect.height;
    const bitmapX = (e.clientX - rect.left) * cssScaleX;
    const bitmapY = (e.clientY - rect.top)  * cssScaleY;
    // Step 2: inverse zoom transform (zoom is applied as setTransform on the ctx,
    // so from the bitmap's perspective: bitmapX = worldX * zoomScale + offsetX)
    const zs = render?.getZoomState?.() ?? { scale: 1, offsetX: 0, offsetY: 0 };
    return {
      canvasX: (bitmapX - zs.offsetX) / zs.scale,
      canvasY: (bitmapY - zs.offsetY) / zs.scale,
    };
  }

  /**
   * Convert a client-space position to bitmap-screen coords WITHOUT zoom reversal.
   * Used by the pinch/wheel handlers that need screen-bitmap space for anchoring.
   */
  function toBitmapCoords(clientX: number, clientY: number): { bitmapX: number; bitmapY: number } | null {
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const cssScaleX = canvas.width / rect.width;
    const cssScaleY = canvas.height / rect.height;
    return {
      bitmapX: (clientX - rect.left) * cssScaleX,
      bitmapY: (clientY - rect.top)  * cssScaleY,
    };
  }

  function handlePointerDown(e: PointerEvent): void {
    // B7: track pointer for pinch detection (before any early returns)
    _pinchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (_pinchPointers.size === 2) {
      // Second finger down → init pinch
      const pts = [..._pinchPointers.values()];
      const p0 = pts[0], p1 = pts[1];
      _pinchInitialDist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      _pinchInitialScale = render?.getZoom?.() ?? 1;
      const midClientX = (p0.x + p1.x) / 2;
      const midClientY = (p0.y + p1.y) / 2;
      const bc = toBitmapCoords(midClientX, midClientY);
      if (bc) {
        const zs = render?.getZoomState?.() ?? { scale: 1, offsetX: 0, offsetY: 0 };
        _pinchInitialAnchorWorldX = (bc.bitmapX - zs.offsetX) / zs.scale;
        _pinchInitialAnchorWorldY = (bc.bitmapY - zs.offsetY) / zs.scale;
      }
      return; // Suppress game-action handling when pinch starts
    }

    // B12: on touch, start long-press timer for tower-stats tooltip.
    if (_isCoarsePointer && e.pointerType !== "mouse") {
      // Cancel any prior long-press.
      if (_longPressTimer !== null) { clearTimeout(_longPressTimer); _longPressTimer = null; }
      // Clear any previously pinned tooltip.
      if (_touchTooltipActive) {
        _touchTooltipActive = false;
        render?.setHoverCell?.(null);
      }
      _longPressStartX = e.clientX;
      _longPressStartY = e.clientY;
      // Snapshot coords for timer closure.
      const downCoords = (() => {
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return null;
        const cssScaleX = canvas.width / rect.width;
        const cssScaleY = canvas.height / rect.height;
        const bitmapX = (e.clientX - rect.left) * cssScaleX;
        const bitmapY = (e.clientY - rect.top)  * cssScaleY;
        const zs = render?.getZoomState?.() ?? { scale: 1, offsetX: 0, offsetY: 0 };
        return { canvasX: (bitmapX - zs.offsetX) / zs.scale, canvasY: (bitmapY - zs.offsetY) / zs.scale };
      })();
      if (downCoords && currentScene() === "game") {
        _longPressTimer = setTimeout(() => {
          _longPressTimer = null;
          if (!getSession) return;
          const sess = getSession();
          if (!sess) return;
          const rows = sess.field.rows;
          const cols = sess.field.cols;
          const cell = pointToGameCell(downCoords.canvasX, downCoords.canvasY, rows, cols);
          if (cell && sess.towers.some(t => t.cell.row === cell.row && t.cell.col === cell.col)) {
            _touchTooltipActive = true;
            render?.setHoverCell?.(cell);
          }
        }, 500);
      }
    }

    const coords = toCanvasCoords(e);
    if (!coords) return;
    const { canvasX, canvasY } = coords;
    const scene = currentScene();
    if (scene !== "game" || !getSession) return;
    const session = getSession();
    if (!session || session.won !== null) return;

    const inv = session.gemInventory ?? [];
    const towersView = session.towers.map((t) => ({
      id: t.id,
      cell: t.cell,
      gemSlots: t.gemSlots.map((s) => ({ gem: s.gem ? { type: s.gem.type, level: s.gem.level } : null })),
    }));
    const invHit = pointToInventorySlot(canvasX, canvasY, inv);
    const towerHit = pointToTowerSlot(canvasX, canvasY, session.field.rows, session.field.cols, towersView);

    if (invHit) {
      dragSource = { kind: "inventory", gemId: invHit.gem.id };
      canvas?.setPointerCapture(e.pointerId);
      updateViewWithDrag?.({
        gem: { id: invHit.gem.id, type: invHit.gem.type, level: invHit.gem.level },
        screenX: canvasX,
        screenY: canvasY,
      });
    } else if (towerHit) {
      selectedTowerId = towerHit.towerId;
      const tower = session.towers.find((t) => t.id === towerHit.towerId);
      const slot = tower?.gemSlots[towerHit.slotIndex];
      const gem = slot?.gem;
      if (gem) {
        dragSource = { kind: "tower", towerId: towerHit.towerId, slotIndex: towerHit.slotIndex, gemId: gem.id };
        canvas?.setPointerCapture(e.pointerId);
        updateViewWithDrag?.({
          gem: { id: gem.id, type: gem.type, level: gem.level },
          screenX: canvasX,
          screenY: canvasY,
        });
      }
    }
  }

  function handlePointerMove(e: PointerEvent): void {
    // B7: update pinch tracking and apply zoom if two fingers active
    if (_pinchPointers.has(e.pointerId)) {
      _pinchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (_pinchPointers.size === 2 && render?.setZoom) {
      const pts = [..._pinchPointers.values()];
      const p0 = pts[0], p1 = pts[1];
      const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      if (_pinchInitialDist > 0) {
        const newScale = Math.max(0.5, Math.min(3, _pinchInitialScale * (dist / _pinchInitialDist)));
        render.setZoom(newScale, _pinchInitialAnchorWorldX, _pinchInitialAnchorWorldY);
      }
      return; // Suppress hover/drag while pinching
    }

    // B12: cancel long-press if finger moved > 8px.
    if (_longPressTimer !== null) {
      const dx = e.clientX - _longPressStartX;
      const dy = e.clientY - _longPressStartY;
      if (Math.hypot(dx, dy) > 8) {
        clearTimeout(_longPressTimer);
        _longPressTimer = null;
      }
    }

    // Update hover cell for ghost preview (game scene only).
    if (render && canvas && currentScene() === "game") {
      const coords = toCanvasCoords(e);
      if (coords) {
        const sess = getSession?.();
        const rows = sess?.field.rows ?? 13;
        const cols = sess?.field.cols ?? 13;
        const cell = pointToGameCell(coords.canvasX, coords.canvasY, rows, cols);
        render.setHoverCell?.(cell);
      } else {
        render.setHoverCell?.(null);
      }
    }
    // Editor hover cell
    if (canvas && currentScene() === "editor" && editorSetHoverCell) {
      const coords = toCanvasCoords(e);
      if (coords) {
        const cell = hitTestEditorCell(coords.canvasX, coords.canvasY, 13, 13, canvas.width, canvas.height);
        editorSetHoverCell(cell);
      } else {
        editorSetHoverCell(null);
      }
    }
    if (!dragSource || !canvas) return;
    const coords = toCanvasCoords(e);
    if (!coords) return;
    const session = getSession?.();
    if (!session) return;
    const gem = dragSource.kind === "inventory"
      ? session.gemInventory.find((g) => g.id === dragSource!.gemId)
      : (() => {
          const t = session.towers.find((x) => x.id === (dragSource as { towerId: string }).towerId);
          return t?.gemSlots[(dragSource as { slotIndex: number }).slotIndex]?.gem ?? null;
        })();
    if (!gem) return;
    updateViewWithDrag?.({
      gem: { id: gem.id, type: gem.type, level: gem.level },
      screenX: coords.canvasX,
      screenY: coords.canvasY,
    });
  }

  function handlePointerUp(e: PointerEvent): void {
    // B7: remove from pinch tracking
    const wasInPinch = _pinchPointers.size === 2;
    _pinchPointers.delete(e.pointerId);
    if (_pinchPointers.size < 2) _pinchInitialDist = 0;
    if (wasInPinch) return; // Don't fire click after pinch ends

    // B12: cancel long-press timer on lift.
    if (_longPressTimer !== null) {
      clearTimeout(_longPressTimer);
      _longPressTimer = null;
    }
    // B12: on touch, if the tooltip was NOT shown by long-press (i.e. quick tap),
    // clear any stale _hoverCell so the tooltip doesn't stick.
    if (_isCoarsePointer && e.pointerType !== "mouse" && !_touchTooltipActive) {
      render?.setHoverCell?.(null);
    }

    const coords = toCanvasCoords(e);
    // Right-click: demolish tower at clicked cell
    if (e.button === 2) {
      if (coords && demolishTower) {
        const scene = currentScene();
        if (scene === "game") {
          const session = getSession?.();
          if (session && session.won === null && coords.canvasX < GAME_GRID_W) {
            const rows = session.field.rows;
            const cols = session.field.cols;
            const cell = pointToGameCell(coords.canvasX, coords.canvasY, rows, cols);
            if (cell) {
              const tower = session.towers.find(
                (t) => t.cell.row === cell.row && t.cell.col === cell.col
              );
              if (tower) demolishTower(tower.id);
            }
          }
        }
      }
      return;
    }
    if (!dragSource) {
      lastPointerShiftKey = e.shiftKey;
      if (coords) handleClick(coords.canvasX, coords.canvasY);
      return;
    }
    canvas?.releasePointerCapture(e.pointerId);
    updateViewWithDrag?.(null);

    if (!coords || !placeGem || !removeGemFromTower || !swapGemsBetweenTowers) {
      dragSource = null;
      return;
    }
    const session = getSession?.();
    if (!session) {
      dragSource = null;
      return;
    }

    const towersView = session.towers.map((t) => ({
      id: t.id,
      cell: t.cell,
      gemSlots: t.gemSlots.map((s) => ({ gem: s.gem ? { type: s.gem.type, level: s.gem.level } : null })),
    }));
    const dropZone = pointToDropZone(coords.canvasX, coords.canvasY, session.field.rows, session.field.cols, towersView, session.gemInventory ?? []);

    if (!dropZone) {
      // Drop missed both tower and inventory → no-op, gem returns to inventory.
      // (Bomb-on-empty-cell removed: fat-finger misses silently consumed gems.)
      dragSource = null;
      return;
    }

    const gemId = dragSource.gemId;
    const srcTowerId = dragSource.kind === "tower" ? dragSource.towerId : null;

    if (dropZone.kind === "inventory") {
      if (srcTowerId !== null) {
        removeGemFromTower(srcTowerId, 0);
      }
    } else {
      const targetTowerId = dropZone.towerId;
      const targetTower = session.towers.find((t) => t.id === targetTowerId);
      const targetHasGem = targetTower?.gemSlots[0]?.gem != null;

      if (srcTowerId === null) {
        if (targetHasGem) {
          /* occupied slot — no-op */
        } else {
          placeGem(gemId, targetTowerId, 0);
        }
      } else if (srcTowerId === targetTowerId) {
        /* drop on same tower: no-op */
      } else {
        if (targetHasGem) {
          /* occupied slot — no-op */
        } else {
          removeGemFromTower(srcTowerId, 0);
          placeGem(gemId, targetTowerId, 0);
        }
      }
    }
    dragSource = null;
  }

  function handlePointerCancel(e: PointerEvent): void {
    // B7: clean up pinch state on cancel
    _pinchPointers.delete(e.pointerId);
    if (_pinchPointers.size < 2) _pinchInitialDist = 0;

    // B12: cancel long-press timer and clear stale hover cell.
    if (_longPressTimer !== null) {
      clearTimeout(_longPressTimer);
      _longPressTimer = null;
    }
    if (_isCoarsePointer && e.pointerType !== "mouse" && !_touchTooltipActive) {
      render?.setHoverCell?.(null);
    }

    if (dragSource) {
      canvas?.releasePointerCapture(e.pointerId);
      updateViewWithDrag?.(null);
      dragSource = null;
    }
  }

  function handleClick(canvasX: number, canvasY: number): void {
    // B12: any tap on canvas while a touch tooltip is active closes it.
    if (_touchTooltipActive) {
      _touchTooltipActive = false;
      render?.setHoverCell?.(null);
    }

    // Tutorial overlay buttons (checked first, in game scene only)
    if (render && getTutorialStep && getTutorialStep() >= 0) {
      if (render.hitTestTutorialNext(canvasX, canvasY)) {
        onTutorialNext?.();
        return;
      }
      if (render.hitTestTutorialSkip(canvasX, canvasY)) {
        onTutorialSkip?.();
        return;
      }
    }

    const scene = currentScene();
    if (scene === "story") {
      if (render?.hitTestStoryNext(canvasX, canvasY)) {
        onStoryNext?.();
      }
      return;
    }

    if (scene === "editor") {
      if (canvas && editorHandleClick) {
        const cell = hitTestEditorCell(canvasX, canvasY, 13, 13, canvas.width, canvas.height);
        if (cell) {
          const mode = getEditorMode?.() ?? "waypoint";
          editorHandleClick(cell, mode);
        }
      }
      return;
    }
    if (scene === "settings") {
      // Settings scene: check hit-tests in order
      if (render?.hitTestStageSettingStart(canvasX, canvasY)) {
        onStageSettingStart?.();
        return;
      }
      if (render?.hitTestStageSettingCancel(canvasX, canvasY)) {
        onStageSettingCancel?.();
        return;
      }
      if (render) {
        const opt = render.hitTestStageSettingOption(canvasX, canvasY);
        if (opt !== null) {
          onStageSettingSelectOption?.(opt.kind, opt.index);
          return;
        }
        const toggle = render.hitTestStageSettingToggle(canvasX, canvasY);
        if (toggle !== null) {
          onStageSettingToggle?.(toggle);
          return;
        }
      }
      return;
    }
    if (scene === "map") {
      // Stats panel close button
      if (render?.hitTestStatsPanelClose(canvasX, canvasY)) {
        onStatsPanelClose?.();
        return;
      }
      // Stats panel toggle button — only when stats panel is closed
      if (!getStatsPanelOpen?.() && render?.hitTestStatsPanelButton(canvasX, canvasY)) {
        onStatsPanelToggle?.();
        return;
      }
      // Backdrop guard: if stats panel is open, swallow clicks
      if (getStatsPanelOpen?.()) return;
      // Skills panel close button
      if (render?.hitTestSkillsPanelClose(canvasX, canvasY)) {
        onSkillsPanelClose?.();
        return;
      }
      // Skill «+» button
      if (render) {
        const skillId = render.hitTestSkillButton(canvasX, canvasY);
        if (skillId !== null) {
          onSkillButtonClick?.(skillId);
          return;
        }
      }
      // Skills panel toggle button — only when panel is closed
      if (!getSkillsPanelOpen?.() && render?.hitTestSkillsPanelButton(canvasX, canvasY)) {
        onSkillsPanelToggle?.();
        return;
      }
      // Backdrop guard: if panel is open, any click outside close/skill buttons is swallowed
      if (getSkillsPanelOpen?.()) return;
      const geometry = getMapClickGeometry();
      if (!geometry) return;
      const cell = pointToMapCell(canvasX, canvasY, canvas!.width, canvas!.height, geometry.rows, geometry.cols);
      if (!cell) return;
      const cellIndex = cell.row * geometry.cols + cell.col;
      const fieldId = geometry.fieldIds[cellIndex];
      if (!fieldId) return;
      if (!geometry.playableFieldIds.includes(fieldId)) return;
      // Shift+Click on a map field → open the authoring editor for that field.
      if (lastPointerShiftKey && openEditor) {
        openEditor(fieldId);
        return;
      }
      if (selectField) {
        selectField(fieldId);
      } else if (openBattleSettings) {
        openBattleSettings(fieldId);
      } else if (startBattle) {
        const result = startBattle(fieldId);
        if ("ok" in result && result.ok === true) gameLoop.start();
      }
      return;
    }
    if (scene !== "game" || !getSession) return;
    const session = getSession();
    if (!session) return;
    if (session.won !== null) {
      exitOrGoToMap?.();
      return;
    }
    // Speed buttons moved to DOM HUD (Phase 2.2) — canvas click in HUD column
    // no longer handled here; all clicks right of GAME_GRID_W are consumed by DOM.
    if (canvasX >= GAME_GRID_W) {
      return;
    }
    const rows = session.field.rows;
    const cols = session.field.cols;
    const cell = pointToGameCell(canvasX, canvasY, rows, cols);
    if (!cell) return;
    const tower = session.towers.find(
      (t) => t.cell.row === cell.row && t.cell.col === cell.col
    );
    if (tower) {
      selectedTowerId = tower.id;
    } else if (placeBuilding) {
      const bt =
        placementMode === "Wall" ? BT.Wall :
        placementMode === "Amplifier" ? BT.Amplifier :
        placementMode === "Trap" ? BT.Trap :
        placementMode === "Shrine" ? BT.Shrine :
        BT.Tower;
      placeBuilding(cell, bt);
    }
  }

  function applyPlacementMode(mode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine"): void {
    placementMode = mode;
    const s = getSession?.();
    if (s) {
      render?.update(sessionToView(s, "game", undefined, placementMode));
    }
  }

  function getMapClickGeometry(): (MapGeometryInput & { playableFieldIds: FieldId[] }) | null {
    const geometry = getMapGeometry?.();
    if (geometry) {
      return {
        ...geometry,
        playableFieldIds: geometry.playableFieldIds ?? geometry.fieldIds,
      };
    }
    const fieldIds = getMapFieldIds?.();
    if (!fieldIds || fieldIds.length !== MAP_ROWS * MAP_COLS) return null;
    return {
      fieldIds,
      rows: MAP_ROWS,
      cols: MAP_COLS,
      playableFieldIds: fieldIds.filter((fieldId) => isFieldOnLand(fieldId)),
    };
  }

  const cleanups: Array<() => void> = [];
  let attached = false;

  return {
    attach() {
      if (attached) return;
      attached = true;

      if (goToMapButton && exitOrGoToMap) {
        const handler = () => exitOrGoToMap();
        goToMapButton.addEventListener("click", handler);
        cleanups.push(() => goToMapButton.removeEventListener("click", handler));
      }

      const keydownHandler = (e: KeyboardEvent) => {
        const scene = currentScene();

        // Editor scene shortcuts
        if (scene === "editor") {
          if (e.code === "KeyW") {
            editorSetMode?.("waypoint");
          } else if (e.code === "KeyB") {
            editorSetMode?.("block");
          } else if (e.code === "KeyC") {
            editorClearWaypoints?.();
          } else if (e.code === "KeyZ") {
            editorRemoveLastWaypoint?.();
          } else if (e.code === "KeyS") {
            editorCloseEditor?.(true);
          } else if (e.code === "Escape") {
            editorCloseEditor?.(false);
          }
          return;
        }

        // Settings scene shortcuts
        if (scene === "settings") {
          if (e.code === "Escape") {
            onStageSettingCancel?.();
          }
          return;
        }

        // Story scene shortcuts
        if (scene === "story") {
          if (e.code === "Space" || e.code === "Enter") {
            onStoryNext?.();
          } else if (e.code === "Escape") {
            onStorySkip?.();
          }
          return;
        }


        // Map-scene shortcuts
        if (scene === "map") {
          if (e.code === "KeyI") {
            onStatsPanelToggle?.();
            return;
          }
          if (e.code === "KeyS" || e.code === "KeyK") {
            onSkillsPanelToggle?.();
            return;
          }
          if (e.code === "Escape") {
            onStatsPanelClose?.();
            onSkillsPanelClose?.();
            return;
          }
          return;
        }

        if (scene !== "game") return;
        const session = getSession?.();
        if (!session) return;
        if (session.won !== null) return;

        const gemType = KEY_TO_GEM[e.code];
        if (gemType && createGem) {
          createGem(gemType);
          return;
        }

        if (e.code === "KeyW") {
          applyPlacementMode(placementMode === "Wall" ? "Tower" : "Wall");
          return;
        }

        if (e.code === "KeyA") {
          applyPlacementMode(placementMode === "Amplifier" ? "Tower" : "Amplifier");
          return;
        }

        if (e.code === "KeyT") {
          applyPlacementMode(placementMode === "Trap" ? "Tower" : "Trap");
          return;
        }

        if (e.code === "KeyR") {
          applyPlacementMode(placementMode === "Shrine" ? "Tower" : "Shrine");
          return;
        }

        if (e.code === "Space") {
          e.preventDefault();
          if (session.speed === 0) {
            // Guard: block first-ever Space when no gem is placed in any tower.
            // "First wave not yet started" = currentWaveIndex === 0 AND no monsters spawned.
            const isPreBattle = session.currentWaveIndex === 0 && session.monsters.length === 0;
            const hasGemInTower = session.towers.some(t => t.gemSlots.some(s => s.gem));
            if (isPreBattle && !hasGemInTower) {
              // Show transient hint via existing achievement-toast mechanism.
              render?.showAchievementToast(t("tutorial.firstGem"));
              return;
            }
            if (resume) resume();
            else setSpeed?.(1);
          } else {
            pause?.();
          }
          return;
        }

        if (e.code === "Escape") {
          selectedTowerId = null;
          return;
        }

        if (e.code === "KeyZ") {
          // Dev-cheat: seed 2 towers with L4 Yellow gems + L4 Red gem in inventory.
          if (!placeBuilding || !createGem || !placeGem || !getSession) return;
          const s = getSession();
          if (!s) return;
          s.mana.max = Math.max(s.mana.max, 100000);
          s.mana.current = s.mana.max;
          // Pick empties that are adjacent to the path (so towers can actually reach monsters).
          const path = s.field.walkableCells ?? [];
          const empties: { row: number; col: number }[] = [];
          for (const pc of path) {
            if (empties.length >= 2) break;
            for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[-1,1],[1,-1],[1,1]]) {
              if (empties.length >= 2) break;
              const r = pc.row + dr, c = pc.col + dc;
              if (r < 0 || r >= s.field.rows || c < 0 || c >= s.field.cols) continue;
              const blocked = s.field.blockedCells?.some((b) => b.row === r && b.col === c);
              const occupied = s.towers.some((t) => t.cell.row === r && t.cell.col === c);
              const onPath = path.some((p) => p.row === r && p.col === c);
              const already = empties.some((e) => e.row === r && e.col === c);
              if (!blocked && !occupied && !onPath && !already) empties.push({ row: r, col: c });
            }
          }
          for (const cell of empties) {
            const res = placeBuilding(cell, BT.Tower);
            if (!("ok" in res && res.ok)) continue;
            const tower = s.towers.find((t) => t.cell.row === cell.row && t.cell.col === cell.col);
            if (!tower) continue;
            const g = createGem(GT.Yellow);
            if (!("ok" in g && g.ok)) continue;
            const freshGem = s.gemInventory?.[s.gemInventory.length - 1];
            if (freshGem) {
              freshGem.level = 4;
              placeGem(freshGem.id, tower.id, 0);
            }
          }
          createGem(GT.Red);
          const red = s.gemInventory?.[s.gemInventory.length - 1];
          if (red) red.level = 4;
          s.mana.current = s.mana.max;
          console.debug("[Z-cheat] seeded", { towers: s.towers.length, inv: s.gemInventory?.length, mana: s.mana.current });
          return;
        }

        if (e.code === "KeyC" && combineGems) {
          if (selectedInvGemA && selectedInvGemB) {
            const result = combineGems(selectedInvGemA, selectedInvGemB);
            if ("code" in result) {
              console.debug("[combine] error:", result.code);
            }
            // Clear selection regardless of success/failure
            selectedInvGemA = null;
            selectedInvGemB = null;
          } else {
            console.debug("[combine] KeyC: need 2 selected inventory gems (have:", selectedInvGemA, selectedInvGemB, ")");
          }
          return;
        }

        if (selectedTowerId) {
          const slot: GemSlot = { towerId: selectedTowerId, slotIndex: 0 };
          if (e.code === "KeyU" && upgradeGem) {
            upgradeGem(slot);
          } else if (e.code === "KeyD" && duplicateGem) {
            duplicateGem(slot);
          } else if (e.code === "KeyX" && recycleGem) {
            recycleGem(slot);
          }
        }
      };
      if (typeof document !== "undefined") {
        document.addEventListener("keydown", keydownHandler);
        cleanups.push(() => document.removeEventListener("keydown", keydownHandler));
      }

      if (canvas) {
        const contextmenuHandler = (e: Event) => e.preventDefault();
        canvas.addEventListener("pointerdown", handlePointerDown);
        canvas.addEventListener("pointermove", handlePointerMove);
        canvas.addEventListener("pointerup", handlePointerUp);
        canvas.addEventListener("pointercancel", handlePointerCancel);
        canvas.addEventListener("contextmenu", contextmenuHandler);

        // ── B7: Wheel zoom ──────────────────────────────────────────────────
        const wheelHandler = (e: WheelEvent) => {
          if (!render?.setZoom) return;
          e.preventDefault();
          const bc = toBitmapCoords(e.clientX, e.clientY);
          if (!bc) return;
          const zs = render.getZoomState?.() ?? { scale: 1, offsetX: 0, offsetY: 0 };
          // Anchor in world-canvas space = inverse of current zoom
          const anchorX = (bc.bitmapX - zs.offsetX) / zs.scale;
          const anchorY = (bc.bitmapY - zs.offsetY) / zs.scale;
          const delta = -Math.sign(e.deltaY) * 0.1;
          const newScale = Math.max(0.5, Math.min(3, zs.scale + delta));
          render.setZoom(newScale, anchorX, anchorY);
        };
        canvas.addEventListener("wheel", wheelHandler, { passive: false });
        // ─────────────────────────────────────── (pinch integrated in handlePointer*)

        cleanups.push(() => {
          canvas.removeEventListener("pointerdown", handlePointerDown);
          canvas.removeEventListener("pointermove", handlePointerMove);
          canvas.removeEventListener("pointerup", handlePointerUp);
          canvas.removeEventListener("pointercancel", handlePointerCancel);
          canvas.removeEventListener("contextmenu", contextmenuHandler);
          canvas.removeEventListener("wheel", wheelHandler);
        });
      }

      // Document-level listeners so drag started on a DOM inventory slot
      // continues to move and drop correctly even when the pointer is outside canvas.
      if (typeof document !== "undefined") {
        const docMove = (e: PointerEvent) => { if (dragSource) handlePointerMove(e); };
        const docUp   = (e: PointerEvent) => { if (dragSource) handlePointerUp(e); };
        const docCancel = (e: PointerEvent) => { if (dragSource) handlePointerCancel(e); };
        document.addEventListener("pointermove", docMove);
        document.addEventListener("pointerup", docUp);
        document.addEventListener("pointercancel", docCancel);
        cleanups.push(() => {
          document.removeEventListener("pointermove", docMove);
          document.removeEventListener("pointerup", docUp);
          document.removeEventListener("pointercancel", docCancel);
        });
      }
    },
    dispose() {
      // B12: cancel any pending long-press timer.
      if (_longPressTimer !== null) {
        clearTimeout(_longPressTimer);
        _longPressTimer = null;
      }
      _touchTooltipActive = false;
      for (const cleanup of cleanups) cleanup();
      cleanups.length = 0;
      attached = false;
    },
    getPlacementMode(): "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine" {
      return placementMode;
    },
    setPlacementMode(mode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine"): void {
      applyPlacementMode(mode);
    },
    getSelectedTowerId(): string | null {
      return selectedTowerId;
    },
    startInventoryDrag(gemId: string, screenX: number, screenY: number): void {
      const session = getSession?.();
      if (!session) return;
      const gem = session.gemInventory.find((g) => g.id === gemId);
      if (!gem) return;
      dragSource = { kind: "inventory", gemId };
      updateViewWithDrag?.({
        gem: { id: gem.id, type: gem.type, level: gem.level },
        screenX,
        screenY,
      });
    },
    selectInventoryGem(gemId: string): void {
      if (selectedInvGemA === gemId || selectedInvGemB === gemId) {
        // Deselect
        if (selectedInvGemA === gemId) selectedInvGemA = null;
        if (selectedInvGemB === gemId) selectedInvGemB = null;
      } else if (!selectedInvGemA) {
        selectedInvGemA = gemId;
      } else if (!selectedInvGemB) {
        selectedInvGemB = gemId;
      } else {
        // Both slots occupied — replace secondary
        selectedInvGemB = gemId;
      }
      console.debug("[combine] selection:", selectedInvGemA, selectedInvGemB);
      if (combineArmed && selectedInvGemA && selectedInvGemB) {
        combineSelectedInventoryGems(selectedInvGemA, selectedInvGemB);
      }
    },
    /** Trigger combine with currently selected inventory gems (same as pressing C). */
    triggerCombine(): void {
      if (!combineGems) return;
      if (selectedInvGemA && selectedInvGemB) {
        combineSelectedInventoryGems(selectedInvGemA, selectedInvGemB);
      } else {
        combineArmed = true;
        console.debug("[combine] triggerCombine: need 2 selected inventory gems (have:", selectedInvGemA, selectedInvGemB, ")");
      }
    },
    combineInventoryGems(gemAId: string, gemBId: string): void {
      combineSelectedInventoryGems(gemAId, gemBId);
    },
  };
}
